import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Stack, Button, Select, MenuItem, FormControl, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { LineChart } from '@mui/x-charts/LineChart';
import DropdownIcon from '../icons/DropdownIcon';
import AddIcon from '../icons/AddIcon';
import { getYourData, getCompetitorTrackingHistory } from '../../../utils/yourDataStore';
import HeatmapDataService from '../../../services/HeatmapDataService';
import { getSession } from '../../../utils/authUtils';

import logger from '../../../utils/logger';

const ChartCard = styled(Box)(({ theme }) => ({
    background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
    borderRadius: '12px',
    padding: '16px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
    border: '1px solid rgba(229, 231, 235, 0.8)',
    marginBottom: '16px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    [theme.breakpoints.up('sm')]: {
        padding: '20px',
        borderRadius: '16px',
        marginBottom: '20px'
    },
    [theme.breakpoints.up('md')]: {
        padding: '28px',
        borderRadius: '20px',
        marginBottom: '24px',
        '&:hover': {
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.06)'
        }
    }
}));

const CardHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '16px',
    [theme.breakpoints.up('sm')]: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '16px',
        marginBottom: '20px'
    },
    [theme.breakpoints.up('md')]: {
        marginBottom: '24px'
    }
}));

const HeaderLeft = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    [theme.breakpoints.up('sm')]: {
        gap: '14px'
    }
}));

const IconWrapper = styled(Box)(({ theme, gradient }) => ({
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    background: gradient || 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
    transition: 'transform 0.3s ease',
    [theme.breakpoints.up('sm')]: {
        width: '44px',
        height: '44px',
        borderRadius: '12px',
        fontSize: '20px'
    },
    [theme.breakpoints.up('md')]: {
        width: '48px',
        height: '48px',
        borderRadius: '14px',
        fontSize: '22px',
        '&:hover': {
            transform: 'rotate(-5deg) scale(1.05)'
        }
    }
}));

const CardTitle = styled(Typography)(({ theme }) => ({
    fontFamily: 'Inter, sans-serif',
    fontSize: '15px',
    fontWeight: 700,
    color: '#111827',
    letterSpacing: '-0.02em',
    [theme.breakpoints.up('sm')]: {
        fontSize: '16px'
    },
    [theme.breakpoints.up('md')]: {
        fontSize: '18px'
    }
}));

const CardSubtitle = styled(Typography)(({ theme }) => ({
    fontFamily: 'Inter, sans-serif',
    fontSize: '11px',
    fontWeight: 500,
    color: '#9ca3af',
    marginTop: '2px',
    [theme.breakpoints.up('sm')]: {
        fontSize: '12px'
    }
}));

const HeaderRight = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    flexWrap: 'wrap',
    [theme.breakpoints.up('sm')]: {
        width: 'auto',
        gap: '12px'
    }
}));

const ControlsRow = styled(Stack)(({ theme }) => ({
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '10px',
    marginBottom: '16px',
    [theme.breakpoints.up('sm')]: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap',
        marginBottom: '20px'
    }
}));

const FilterSelect = styled(FormControl)(({ theme }) => ({
    minWidth: '100%',
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb',
        fontSize: '13px',
        fontWeight: 500,
        color: '#374151',
        fontFamily: 'Inter, sans-serif',
        transition: 'all 0.2s ease',
        height: '44px',
        '& fieldset': {
            border: 'none'
        },
        '&:hover': {
            borderColor: '#3b82f6',
            backgroundColor: '#f0f9ff'
        },
        '&.Mui-focused': {
            borderColor: '#3b82f6',
            backgroundColor: '#ffffff'
        }
    },
    '& .MuiSelect-select': {
        padding: '10px 14px',
        paddingRight: '32px'
    },
    [theme.breakpoints.up('sm')]: {
        minWidth: 140,
        flex: 'none',
        '& .MuiOutlinedInput-root': {
            height: '40px'
        }
    }
}));

const KeywordBadge = styled(Box)(({ theme }) => ({
    display: 'none',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 14px',
    borderRadius: '20px',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    color: '#ffffff',
    fontFamily: 'Inter, sans-serif',
    fontSize: '12px',
    fontWeight: 700,
    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.35)',
    transition: 'all 0.3s ease',
    [theme.breakpoints.up('sm')]: {
        display: 'flex',
        fontSize: '13px',
        padding: '10px 18px',
        borderRadius: '25px'
    },
    [theme.breakpoints.up('md')]: {
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 16px rgba(99, 102, 241, 0.45)'
        }
    }
}));

const AddButton = styled(Button)(({ theme }) => ({
    color: '#6b7280',
    fontFamily: 'Inter, sans-serif',
    fontSize: '13px',
    fontWeight: 500,
    textTransform: 'none',
    padding: '8px 14px',
    gap: '6px',
    position: 'relative',
    borderRadius: '10px',
    border: '1px dashed #d1d5db',
    background: '#fafafa',
    '&:hover': {
        backgroundColor: '#f3f4f6',
        borderColor: '#9ca3af'
    }
}));

const ComingSoonBadge = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: '-6px',
    right: '-6px',
    backgroundColor: '#f59e0b',
    color: '#ffffff',
    fontSize: '8px',
    fontWeight: 700,
    fontFamily: 'Inter, sans-serif',
    padding: '2px 5px',
    borderRadius: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
    zIndex: 1
}));

const ChartWrapper = styled(Box)(({ theme }) => ({
    borderRadius: '10px',
    backgroundColor: '#fafafa',
    padding: '12px',
    marginBottom: '16px',
    border: '1px solid #f1f5f9',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    [theme.breakpoints.up('sm')]: {
        padding: '16px',
        borderRadius: '12px',
        marginBottom: '20px'
    },
    [theme.breakpoints.up('md')]: {
        padding: '20px',
        overflowX: 'visible'
    }
}));

const LegendRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    gap: '20px',
    marginBottom: '16px',
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
        gap: '12px'
    }
}));

const LegendItem = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
});

const LegendDot = styled(Box)(({ color }) => ({
    width: '10px',
    height: '10px',
    borderRadius: '3px',
    backgroundColor: color
}));

const LegendLabel = styled(Typography)({
    fontFamily: 'Inter, sans-serif',
    fontSize: '11px',
    fontWeight: 500,
    color: '#6b7280'
});

const FooterRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: '16px',
    borderTop: '1px solid #f1f5f9',
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        gap: '12px'
    }
}));

const FooterInfo = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
});

const InfoItem = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
});

const InfoIcon = styled(Box)({
    fontSize: '14px'
});

const InfoText = styled(Typography)({
    fontFamily: 'Inter, sans-serif',
    fontSize: '12px',
    color: '#6b7280'
});

const ViewDataButton = styled(Button)(({ theme }) => ({
    color: '#ffffff',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    fontWeight: 700,
    textTransform: 'none',
    borderRadius: '12px',
    padding: '12px 24px',
    boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
        boxShadow: '0 6px 20px rgba(99, 102, 241, 0.45)',
        transform: 'translateY(-2px)'
    }
}));

const GeoRankChart = ({ geoRankData, onViewGridData, onAddCampaign, currentProfile, keyword }) => {
    const [selectedKeyword, setSelectedKeyword] = useState('');
    const [selectedFrequency, setSelectedFrequency] = useState('Monthly');
    const [heatmapHistory, setHeatmapHistory] = useState([]);
    const [availableKeywords, setAvailableKeywords] = useState([]);
    const [isRealData, setIsRealData] = useState(false);
    const [chartWidth, setChartWidth] = useState(300);
    const chartContainerRef = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    // Responsive chart dimensions
    useEffect(() => {
        const updateChartWidth = () => {
            if (chartContainerRef.current) {
                const containerWidth = chartContainerRef.current.offsetWidth;
                // Account for padding (24px on mobile, 32px on tablet, 40px on desktop)
                const padding = isMobile ? 24 : isTablet ? 32 : 40;
                const calculatedWidth = Math.max(280, containerWidth - padding);
                setChartWidth(calculatedWidth);
            }
        };

        updateChartWidth();

        const resizeObserver = new ResizeObserver(updateChartWidth);
        if (chartContainerRef.current) {
            resizeObserver.observe(chartContainerRef.current);
        }

        window.addEventListener('resize', updateChartWidth);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateChartWidth);
        };
    }, [isMobile, isTablet]);

    // Load competitor tracking data from database (heatmap_data table)
    useEffect(() => {
        const loadHistoryData = async () => {
            try {
                const profileId = currentProfile?.locationId || currentProfile?.id;
                const businessName = currentProfile?.title || currentProfile?.name || '';

                logger.log('[GeoRankChart] Loading data from database for profile:', profileId);

                // Get user ID (support admin access)
                let userId;
                try {
                    const adminAccessData = sessionStorage.getItem('adminAccessData');
                    if (adminAccessData) {
                        const accessInfo = JSON.parse(adminAccessData);
                        userId = accessInfo.targetUserId;
                        logger.log('[GeoRankChart] Admin viewing mode - using client user ID:', userId);
                    }
                } catch (_) {}

                if (!userId) {
                    const session = getSession();
                    userId = session?.id || session?.user?.id;
                }

                if (userId) {
                    const queryParams = {};
                    if (profileId) {
                        queryParams.location_id = profileId;
                    }
                    if (businessName) {
                        queryParams.business_name = businessName;
                    }

                    logger.log('[GeoRankChart] Fetching from database with:', { userId, queryParams });

                    let response = null;
                    try {
                        response = await HeatmapDataService.getHistory(userId, queryParams);
                        logger.log('[GeoRankChart] getHistory returned:', response);
                    } catch (fetchError) {
                        logger.error('[GeoRankChart] Error calling getHistory:', fetchError);
                        response = null;
                    }

                    if (response?.success && response?.data && response.data.length > 0) {
                        logger.log('[GeoRankChart] ✅ Found', response.data.length, 'heatmap records in database');

                        // Convert DB records to the format expected by the chart
                        const dbHistory = response.data.map(record => ({
                            keyword: record.keyword,
                            businessName: record.business_name,
                            greenGridPoints: record.green_points || 0,
                            yellowGridPoints: record.yellow_points || 0,
                            redGridPoints: record.red_points || 0,
                            grayGridPoints: record.gray_points || 0,
                            totalGridPoints: record.total_points || 0,
                            date: record.analysis_date,
                            timestamp: new Date(record.analysis_date).getTime(),
                            month: new Date(record.analysis_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
                            daily: new Date(record.analysis_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                        }));

                        setHeatmapHistory(dbHistory);
                        setIsRealData(true);

                        // Extract unique keywords
                        const keywords = [...new Set(dbHistory.map(entry => entry.keyword).filter(Boolean))];
                        setAvailableKeywords(keywords);

                        // Set default keyword
                        if (keywords.length > 0) {
                            setSelectedKeyword(keywords[0]);
                        }
                    } else {
                        logger.log('[GeoRankChart] No data in database, trying localStorage fallback...');

                        // Fallback to localStorage if database is empty
                        const competitorHistory = getCompetitorTrackingHistory(profileId);

                        if (competitorHistory.length > 0) {
                            logger.log('[GeoRankChart] Found localStorage data:', competitorHistory.length, 'entries');
                            setHeatmapHistory(competitorHistory);
                            setIsRealData(true);

                            const keywords = [...new Set(competitorHistory.map(entry => entry.keyword).filter(Boolean))];
                            setAvailableKeywords(keywords);

                            // Set default keyword
                            if (keywords.length > 0) {
                                setSelectedKeyword(keywords[0]);
                            }
                        } else {
                            logger.log('[GeoRankChart] No data found in database or localStorage');
                            setHeatmapHistory([]);
                            setAvailableKeywords([]);
                            setIsRealData(false);
                        }
                    }
                } else {
                    logger.log('[GeoRankChart] No userId available');
                    setHeatmapHistory([]);
                    setAvailableKeywords([]);
                    setIsRealData(false);
                }
            } catch (error) {
                logger.error('[GeoRankChart] Error loading history data:', error);
                setHeatmapHistory([]);
                setAvailableKeywords([]);
                setIsRealData(false);
            }
        };

        loadHistoryData();
    }, [currentProfile?.title, currentProfile?.name, currentProfile?.displayName, currentProfile?.locationId, currentProfile?.id]);

    // Update selected keyword if prop changes
    useEffect(() => {
        if (keyword) {
            setSelectedKeyword(keyword);
            // Ensure availableKeywords includes this prop keyword if not already
            setAvailableKeywords(prev => {
                if (!prev.includes(keyword)) return [keyword, ...prev];
                return prev;
            });
        }
    }, [keyword]);

    // Listen for storage changes to update chart when new data is added
    useEffect(() => {
        const handleStorageChange = async () => {
            logger.log('Storage changed, refreshing chart data...');
            const profileId = currentProfile?.locationId || currentProfile?.id;
            const businessName = currentProfile?.title || currentProfile?.name || '';

            // Get competitor tracking history ONLY
            const competitorHistory = getCompetitorTrackingHistory(profileId);

            if (competitorHistory.length > 0) {
                setHeatmapHistory(competitorHistory);
                setIsRealData(true);

                const keywords = [...new Set(competitorHistory.map(entry => entry.keyword).filter(Boolean))];
                setAvailableKeywords(keywords);

                // Update selected keyword if it's new
                if (keywords.length > 0 && !selectedKeyword) {
                    setSelectedKeyword(keywords[0]);
                }
            } else {
                // Try fetching from database
                try {
                    let userId;
                    try {
                        const adminAccessData = sessionStorage.getItem('adminAccessData');
                        if (adminAccessData) {
                            const accessInfo = JSON.parse(adminAccessData);
                            userId = accessInfo.targetUserId;
                        }
                    } catch (_) {}

                    if (!userId) {
                        const session = getSession();
                        userId = session?.id || session?.user?.id;
                    }

                    if (userId) {
                        const queryParams = {};
                        if (profileId) queryParams.location_id = profileId;
                        if (businessName) queryParams.business_name = businessName;

                        const response = await HeatmapDataService.getHistory(userId, queryParams);

                        if (response?.success && response?.data && response.data.length > 0) {
                            const dbHistory = response.data.map(record => ({
                                keyword: record.keyword,
                                businessName: record.business_name,
                                greenGridPoints: record.green_points || 0,
                                yellowGridPoints: record.yellow_points || 0,
                                redGridPoints: record.red_points || 0,
                                grayGridPoints: record.gray_points || 0,
                                totalGridPoints: record.total_points || 0,
                                date: record.analysis_date,
                                timestamp: new Date(record.analysis_date).getTime(),
                                month: new Date(record.analysis_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
                                daily: new Date(record.analysis_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            }));

                            setHeatmapHistory(dbHistory);
                            setIsRealData(true);

                            const keywords = [...new Set(dbHistory.map(entry => entry.keyword).filter(Boolean))];
                            setAvailableKeywords(keywords);

                            if (keywords.length > 0 && !selectedKeyword) {
                                setSelectedKeyword(keywords[0]);
                            }
                        } else {
                            setHeatmapHistory([]);
                            setAvailableKeywords([]);
                            setIsRealData(false);
                        }
                    }
                } catch (dbError) {
                    logger.error('[GeoRankChart] Error fetching from database on storage change:', dbError);
                    setHeatmapHistory([]);
                    setAvailableKeywords([]);
                    setIsRealData(false);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);

        // Also check for updates when component becomes visible
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                handleStorageChange();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [selectedKeyword, currentProfile?.title, currentProfile?.name, currentProfile?.displayName, currentProfile?.locationId, currentProfile?.id]);

    // Recalculate chart data when frequency changes
    useEffect(() => {
        // Force re-render when frequency changes
        logger.log(`Frequency changed to: ${selectedFrequency}`);
    }, [selectedFrequency]);

    // Group and aggregate data based on selected frequency
    const groupDataByFrequency = (data, frequency) => {
        const grouped = {};

        data.forEach(entry => {
            let key;
            switch (frequency) {
                case 'Daily':
                    key = entry.daily || new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                    break;
                case 'Weekly':
                    key = entry.weekly || `Week ${entry.weekNumber || 1} ${entry.year || new Date(entry.date).getFullYear()}`;
                    break;
                case 'Monthly':
                default:
                    key = entry.monthly || entry.month || new Date(entry.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                    break;
            }

            if (!grouped[key]) {
                grouped[key] = {
                    key,
                    entries: [],
                    totalGreenPoints: 0,
                    totalYellowPoints: 0,
                    totalRedPoints: 0,
                    totalGrayPoints: 0,
                    count: 0,
                    timestamp: entry.timestamp
                };
            }

            grouped[key].entries.push(entry);
            grouped[key].totalGreenPoints += entry.greenGridPoints || 0;
            grouped[key].totalYellowPoints += entry.yellowGridPoints || 0;
            grouped[key].totalRedPoints += entry.redGridPoints || 0;
            grouped[key].totalGrayPoints += entry.grayGridPoints || 0;
            grouped[key].count += 1;

            // Keep the latest timestamp for sorting
            if (entry.timestamp > grouped[key].timestamp) {
                grouped[key].timestamp = entry.timestamp;
            }
        });

        // Calculate averages and return sorted array
        return Object.values(grouped)
            .map(group => ({
                ...group,
                averageGreenPoints: Math.round(group.totalGreenPoints / group.count),
                averageYellowPoints: Math.round(group.totalYellowPoints / group.count),
                averageRedPoints: Math.round(group.totalRedPoints / group.count),
                averageGrayPoints: Math.round(group.totalGrayPoints / group.count)
            }))
            .sort((a, b) => a.timestamp - b.timestamp);
    };

    // Prepare chart data based on selected keyword and frequency
    const getChartData = () => {
        if (!isRealData || heatmapHistory.length === 0) {
            // Fallback to mock data with single series
            return {
                series: [
                    {
                        data: geoRankData.map(item => item.value),
                        color: '#0B91D6',
                        label: 'Mock Performance'
                    }
                ],
                xAxisData: geoRankData.map(item => item.month),
                maxValue: 80
            };
        }

        // Filter history by selected keyword
        const filteredHistory = selectedKeyword
            ? heatmapHistory.filter(entry => entry.keyword === selectedKeyword)
            : heatmapHistory;

        if (filteredHistory.length === 0) {
            return {
                series: [],
                xAxisData: [],
                maxValue: 25
            };
        }

        // Group data by selected frequency
        const groupedData = groupDataByFrequency(filteredHistory, selectedFrequency);

        // Limit data points based on frequency
        let limitedData;
        switch (selectedFrequency) {
            case 'Daily':
                limitedData = groupedData.slice(-30); // Last 30 days
                break;
            case 'Weekly':
                limitedData = groupedData.slice(-12); // Last 12 weeks
                break;
            case 'Monthly':
            default:
                limitedData = groupedData.slice(-12); // Last 12 months
                break;
        }

        const greenData = limitedData.map(group => group.averageGreenPoints);
        const yellowData = limitedData.map(group => group.averageYellowPoints);
        const redData = limitedData.map(group => group.averageRedPoints);
        const grayData = limitedData.map(group => group.averageGrayPoints);
        const xAxisData = limitedData.map(group => group.key);

        // Calculate max value based on all data (with some padding)
        const allValues = [...greenData, ...yellowData, ...redData, ...grayData];
        const maxDataValue = Math.max(...allValues, 25);
        const maxValue = Math.ceil(maxDataValue * 1.2);

        // Create series for each grid point type with heatmap colors
        const series = [
            {
                data: greenData,
                color: '#34A853', // Green - same as heatmap
                label: 'Green Grid Points (Rank 1-3)',
                curve: 'linear'
            },
            {
                data: yellowData,
                color: '#FBBC04', // Yellow - same as heatmap
                label: 'Yellow Grid Points (Rank 4-7)',
                curve: 'linear'
            },
            {
                data: redData,
                color: '#EA4335', // Red - same as heatmap
                label: 'Red Grid Points (Rank 8-19)',
                curve: 'linear'
            },
            {
                data: grayData,
                color: '#9E9E9E', // Gray - same as heatmap
                label: 'Gray Grid Points (Rank 20+)',
                curve: 'linear'
            }
        ].filter(s => s.data.some(val => val > 0)); // Only include series with data

        return { series, xAxisData, maxValue };
    };

    const { series, xAxisData, maxValue } = getChartData();

    const legendData = [
        { label: 'Rank 1-3 (Top)', color: '#10b981' },
        { label: 'Rank 4-7 (Good)', color: '#f59e0b' },
        { label: 'Rank 8-19 (Fair)', color: '#ef4444' },
        { label: 'Rank 20+ (Poor)', color: '#9ca3af' }
    ];

    return (
        <ChartCard>
            <CardHeader>
                <HeaderLeft>
                    <IconWrapper gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)">
                        🗺️
                    </IconWrapper>
                    <Box>
                        <CardTitle>Geo Rank Heatmap</CardTitle>
                        <CardSubtitle>Track your ranking over time • From Competitor Analysis</CardSubtitle>
                    </Box>
                </HeaderLeft>
                <HeaderRight>
                    {(isRealData && selectedKeyword) || keyword ? (
                        <KeywordBadge>
                            🔍 {selectedKeyword || keyword}
                        </KeywordBadge>
                    ) : null}
                    <AddButton onClick={onAddCampaign}>
                        <ComingSoonBadge>Soon</ComingSoonBadge>
                        <AddIcon width={12} height={12} color="#6b7280" />
                        Add Campaign
                    </AddButton>
                </HeaderRight>
            </CardHeader>

            <ControlsRow>
                <FilterSelect size="small">
                    <Select
                        value={selectedKeyword || (availableKeywords[0] || 'No keywords')}
                        onChange={(e) => setSelectedKeyword(e.target.value)}
                        IconComponent={() => <DropdownIcon width={9} height={5} color="#6b7280" />}
                        disabled={!isRealData || availableKeywords.length === 0}
                    >
                        {isRealData && availableKeywords.length > 0 ? (
                            availableKeywords.map(keyword => (
                                <MenuItem key={keyword} value={keyword}>{keyword}</MenuItem>
                            ))
                        ) : (
                            <MenuItem value="No keywords">No keywords analyzed</MenuItem>
                        )}
                    </Select>
                </FilterSelect>

                <FilterSelect size="small">
                    <Select
                        value={selectedFrequency}
                        onChange={(e) => setSelectedFrequency(e.target.value)}
                        IconComponent={() => <DropdownIcon width={9} height={5} color="#6b7280" />}
                    >
                        <MenuItem value="Daily">Daily</MenuItem>
                        <MenuItem value="Weekly">Weekly</MenuItem>
                        <MenuItem value="Monthly">Monthly</MenuItem>
                    </Select>
                </FilterSelect>
            </ControlsRow>

            <LegendRow>
                {legendData.map((item, index) => (
                    <LegendItem key={index}>
                        <LegendDot color={item.color} />
                        <LegendLabel>{item.label}</LegendLabel>
                    </LegendItem>
                ))}
            </LegendRow>

            <ChartWrapper ref={chartContainerRef}>
                <LineChart
                    width={chartWidth}
                    height={isMobile ? 250 : isTablet ? 300 : 350}
                    series={series}
                    xAxis={[{
                        scaleType: 'point',
                        data: xAxisData,
                        tickLabelStyle: {
                            fontSize: isMobile ? 9 : 11,
                            angle: isMobile ? -45 : 0,
                            textAnchor: isMobile ? 'end' : 'middle'
                        }
                    }]}
                    yAxis={[{
                        min: 0,
                        max: maxValue,
                        tickNumber: isMobile ? 4 : 5
                    }]}
                    margin={{
                        left: isMobile ? 40 : 50,
                        right: isMobile ? 15 : 30,
                        top: 20,
                        bottom: isMobile ? 70 : 60
                    }}
                    grid={{ horizontal: true, vertical: false }}
                    legend={{ hidden: true }}
                />
            </ChartWrapper>

            <FooterRow>
                <FooterInfo>
                    <InfoItem>
                        <InfoIcon>📊</InfoIcon>
                        <InfoText>
                            {isRealData
                                ? `${selectedFrequency} data • ${heatmapHistory.length} records`
                                : 'No data yet - Run competitor analysis to see data'}
                        </InfoText>
                    </InfoItem>
                    {isRealData && (
                        <InfoItem>
                            <InfoIcon>📅</InfoIcon>
                            <InfoText>
                                Last updated: {new Date(heatmapHistory[heatmapHistory.length - 1]?.timestamp || Date.now()).toLocaleDateString()}
                            </InfoText>
                        </InfoItem>
                    )}
                </FooterInfo>
                <ViewDataButton onClick={onViewGridData}>
                    View Grid Data →
                </ViewDataButton>
            </FooterRow>
        </ChartCard>
    );
};

export default GeoRankChart;